import { useTranslation } from 'next-i18next';
import { cn } from '@/utils';
import { Button, type ButtonProps } from '../button';
import { DefaultDialog, type DefaultDialogProps } from './DefaultDialog';
import { createDialogManager } from './utils';

/**
 * @returns {string}
 * @example
 * const id = confirmDialog.open({
 * // ...
 * });
 * confirmDialog.close(id);
 *
 * @returns {Promise<boolean>}
 * @example
 * const confirm = await confirmDialog.openAsync({
 *  size: 'xs',
 *  header: {
 *    title: 'Confirm',
 *    topSlot: '❗️',
 *  },
 *  body: 'Are you sure?',
 *  cancelText: 'Cancel',
 *  cancelIntent: 'neutral',
 *  confirmText: 'Confirm',
 *  confirmIntent: 'destructive',
 *  shouldCloseOnDimClick: false,
 * });
 * if (confirm) {
 *  console.log('confirmed');
 * } else {
 *  console.log('cancelled');
 * }
 */
export const confirmDialog = createDialogManager<ConfirmDialogProps>(options => <ConfirmDialog {...options} />);
export interface ConfirmDialogProps extends Omit<DefaultDialogProps, 'footer'> {
  confirmText?: string;
  confirmIntent?: ButtonProps['intent'];
  cancelText?: string;
  cancelIntent?: ButtonProps['intent'];
  footerClassName?: string;
}
export const ConfirmDialog = (props: ConfirmDialogProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    cancelText: cancelTextProp,
    confirmText: confirmTextProp,
    cancelIntent = 'neutral',
    confirmIntent = 'strong',
    footerClassName
  } = props;
  const cancelText = cancelTextProp ?? t('common.confirmDialog.cancelText');
  const confirmText = confirmTextProp ?? t('common.confirmDialog.confirmText');
  return <DefaultDialog onClose={props.onClose} size={props.size} className={props.className} header={props.header} body={<div className="px-lg max-mobile-lg:px-md">{props.body}</div>} footer={<div className={cn('flex gap-xs', footerClassName)}>
          <Button className="w-full" size="md" intent={cancelIntent} onClick={() => props.onClose?.(false)}>
            {cancelText}
          </Button>
          <Button className="w-full" size="md" intent={confirmIntent} onClick={() => props.onClose?.(true)}>
            {confirmText}
          </Button>
        </div>} hasDrawerHandle={props.hasDrawerHandle} hasDim={props.hasDim} shouldCloseOnDimClick={props.shouldCloseOnDimClick} forceDrawerOnMobile={props.forceDrawerOnMobile ?? false} data-sentry-element="DefaultDialog" data-sentry-component="ConfirmDialog" data-sentry-source-file="ConfirmDialog.tsx" />;
};